import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DashboardResponse } from '../models/dashboard.response';
import client from '../clients/client';
import { ChangeDashboardFeaturesRequest } from '../requests/change.dashboard.features.request';
import { Status } from '../redux/redux.status';
import { State } from '../redux/redux.state';

type DashboardState = State<DashboardResponse> & {
  changeDashboardStatus: Status;
  changeDashboardResponse: { message: string };
};

const initialState: DashboardState = {
  data: { conversion_to_sales: true, purchases: true, returns: true },
  status: 'idle',
  changeDashboardStatus: 'idle',
  changeDashboardResponse: { message: '' },
};

export const getDashboardFeatures = createAsyncThunk<DashboardResponse, string>(
  'getDashboardFeatures',
  async (shopId: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`/dashboard/${shopId}`);
      return data;
    } catch (error: any) {
      const { status, data } = error.reponse;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const changeDashboardFeatures = createAsyncThunk<
  { message: string },
  ChangeDashboardFeaturesRequest
>(
  'changeDashboardFeatures',
  async (request: ChangeDashboardFeaturesRequest, { rejectWithValue }) => {
    try {
      const { data } = await client.post(`/dashboard`, request);
      return data;
    } catch (error: any) {
      const { status, data } = error.reponse;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboardSlice',
  initialState,
  reducers: {
    updateConversionToSale: (state, action: PayloadAction<boolean>) => {
      state.data.conversion_to_sales = action.payload;
    },
    updatePurchases: (state, action: PayloadAction<boolean>) => {
      state.data.purchases = action.payload;
    },
    updateReturns: (state, action: PayloadAction<boolean>) => {
      state.data.returns = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getDashboardFeatures.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getDashboardFeatures.fulfilled, (state, action) => {
        state.status = 'successful';
        state.data = action.payload;
      })
      .addCase(getDashboardFeatures.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(changeDashboardFeatures.pending, (state) => {
        state.changeDashboardStatus = 'loading';
      })
      .addCase(changeDashboardFeatures.fulfilled, (state, action) => {
        state.changeDashboardStatus = 'successful';
        state.changeDashboardResponse = action.payload;
      })
      .addCase(changeDashboardFeatures.rejected, (state, action) => {
        state.changeDashboardStatus = 'error';
        state.errors = action.payload;
      }),
});

export const { updateConversionToSale, updatePurchases, updateReturns } =
  dashboardSlice.actions;

export default dashboardSlice.reducer;
